/* ====================================================================
   YAMAKITS — Web components portés du thème Impact
   marquee-text · scrolling-text · impact-text · images-with-text-scrolling
   Portage autonome (sans le bundle Impact) : helpers vanilla équivalents
   à motion (inView / animate / timeline) utilisés par le thème d'origine.
   ==================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------ */

  // Équivalent motion inView(el, cb, {margin})
  function inView(el, callback, options) {
    var margin = (options && options.margin) || '0px';
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          callback(entry);
        }
      });
    }, { rootMargin: margin });
    observer.observe(el);
    return function () { observer.disconnect(); };
  }

  // Throttle sur requestAnimationFrame (équivalent utils Impact)
  function throttle(callback) {
    var requested = false;
    return function () {
      if (requested) return;
      requested = true;
      requestAnimationFrame(function () {
        requested = false;
        callback();
      });
    };
  }

  // cubic-bezier(p1x, p1y, p2x, p2y) → fonction easing t→y (pour le count-up)
  function cubicBezier(p1x, p1y, p2x, p2y) {
    function A(a1, a2) { return 1 - 3 * a2 + 3 * a1; }
    function B(a1, a2) { return 3 * a2 - 6 * a1; }
    function C(a1) { return 3 * a1; }
    function calc(t, a1, a2) { return ((A(a1, a2) * t + B(a1, a2)) * t + C(a1)) * t; }
    function slope(t, a1, a2) { return 3 * A(a1, a2) * t * t + 2 * B(a1, a2) * t + C(a1); }
    return function (x) {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      var t = x;
      for (var i = 0; i < 5; i++) {
        var s = slope(t, p1x, p2x);
        if (s === 0) break;
        t -= (calc(t, p1x, p2x) - x) / s;
      }
      return calc(t, p1y, p2y);
    };
  }

  function toBezierString(easing) {
    if (Array.isArray(easing)) {
      return 'cubic-bezier(' + easing.join(',') + ')';
    }
    return easing || 'ease';
  }

  // animate(el, props, opts) façon motion : keyframes implicites depuis
  // l'état courant, fige la valeur finale en inline style à la fin.
  function animateTo(el, props, opts) {
    opts = opts || {};
    var keyframes = {};
    Object.keys(props).forEach(function (prop) {
      var value = props[prop];
      if (Array.isArray(value)) {
        keyframes[prop] = value[0] === null
          ? [value[1]]           // [null, x] → keyframe implicite vers x
          : value;
      } else {
        keyframes[prop] = [value];
      }
    });
    var animation = el.animate(keyframes, {
      duration: (opts.duration || 0.3) * 1000,
      delay: (opts.delay || 0) * 1000,
      easing: toBezierString(opts.easing),
      fill: 'forwards'
    });
    animation.finished.then(function () {
      Object.keys(keyframes).forEach(function (prop) {
        var frames = keyframes[prop];
        el.style[prop === 'clipPath' ? 'clipPath' : prop] = frames[frames.length - 1];
      });
      animation.cancel();
    }).catch(function () {});
    return animation;
  }

  // animate(progressCallback, {duration, easing}) façon motion
  function animateProgress(callback, opts) {
    opts = opts || {};
    var duration = (opts.duration || 1) * 1000;
    var ease = Array.isArray(opts.easing)
      ? cubicBezier(opts.easing[0], opts.easing[1], opts.easing[2], opts.easing[3])
      : function (t) { return t; };
    var start = null;
    var resolveFinished;
    var finished = new Promise(function (resolve) { resolveFinished = resolve; });
    function frame(now) {
      if (start === null) start = now;
      var progress = Math.min(1, (now - start) / duration);
      callback(ease(progress));
      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        resolveFinished();
      }
    }
    requestAnimationFrame(frame);
    return { finished: finished };
  }

  // Reveal des titres [reveal-on-scroll="true"] (équivalent
  // getHeadingKeyframe en mode "fade" — pas de split-lines hors Impact)
  function revealHeading(heading) {
    if (!heading) return;
    animateTo(heading, { opacity: [0, 1] }, { duration: 0.2 });
  }

  /* ------------------------------------------------------------------
     <marquee-text> — portage verbatim
     ------------------------------------------------------------------ */
  var MarqueeText = (function () {
    function Ctor() {
      var self = Reflect.construct(HTMLElement, [], Ctor);
      if (window.ResizeObserver) {
        new ResizeObserver(self._calculateDuration.bind(self)).observe(self);
      }
      return self;
    }
    Ctor.prototype = Object.create(HTMLElement.prototype, { constructor: { value: Ctor } });
    Object.setPrototypeOf(Ctor, HTMLElement);

    Ctor.prototype._calculateDuration = function (entries) {
      var scrollingSpeed = parseInt(this.getAttribute('scrolling-speed') || 5, 10);
      var contentWidth = entries[0].contentRect.width;
      var span = entries[0].target.querySelector('span');
      if (!span || contentWidth === 0) return;
      var slowFactor = 1 + (Math.min(1600, contentWidth) - 375) / (1600 - 375);
      this.style.setProperty(
        '--marquee-animation-duration',
        (scrollingSpeed * slowFactor * span.clientWidth / contentWidth).toFixed(3) + 's'
      );
    };
    return Ctor;
  })();

  /* ------------------------------------------------------------------
     <scrolling-text> — translation liée au scroll (mode "on scroll")
     ------------------------------------------------------------------ */
  var ScrollingTextEl = (function () {
    function Ctor() {
      return Reflect.construct(HTMLElement, [], Ctor);
    }
    Ctor.prototype = Object.create(HTMLElement.prototype, { constructor: { value: Ctor } });
    Object.setPrototypeOf(Ctor, HTMLElement);

    Ctor.prototype.connectedCallback = function () {
      if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return;
      var keyframes = {
        transform: [
          'translateX(calc(var(--transform-logical-flip) * 50vw))',
          'translateX(calc(var(--transform-logical-flip) * 50vw - 10%))'
        ]
      };
      // ScrollTimeline natif si dispo (comportement Impact), sinon fallback rAF
      if (typeof ViewTimeline === 'function') {
        try {
          var timeline = new ViewTimeline({ subject: this, axis: 'block' });
          this.animate(keyframes, { timeline: timeline, rangeStart: 'cover 0%', rangeEnd: 'cover 100%' });
          return;
        } catch (e) { /* fallback ci-dessous */ }
      }
      var animation = this.animate(keyframes, { duration: 1000, fill: 'both' });
      animation.pause();
      var el = this;
      var update = throttle(function () {
        var rect = el.getBoundingClientRect();
        var viewportHeight = window.innerHeight;
        var progress = (viewportHeight - rect.top) / (viewportHeight + rect.height);
        animation.currentTime = Math.max(0, Math.min(1, progress)) * 1000;
      });
      window.addEventListener('scroll', update, { passive: true });
      update();
    };
    return Ctor;
  })();

  /* ------------------------------------------------------------------
     <impact-text> — apparition + count-up (portage verbatim)
     ------------------------------------------------------------------ */
  var ImpactText = (function () {
    function Ctor() {
      return Reflect.construct(HTMLElement, [], Ctor);
    }
    Ctor.prototype = Object.create(HTMLElement.prototype, { constructor: { value: Ctor } });
    Object.setPrototypeOf(Ctor, HTMLElement);

    Ctor.prototype.connectedCallback = function () {
      if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
        return;
      }
      var self = this;
      inView(this, function (entry) { self._onBecameVisible(entry.target); }, { margin: '-100px' });
    };

    Ctor.prototype._onBecameVisible = function (target) {
      animateTo(target, { opacity: 1, transform: ['translateY(10px)', 'translateY(0)'] }, { duration: 0.3 });

      if (!this.hasAttribute('count-up')) return;

      var itemToSearch = this.childElementCount === 0 ? this : this.firstElementChild;
      var matches = itemToSearch.textContent.trim().match(/\d+(?:[,. ]\d+)*/);
      if (!matches) return;

      itemToSearch.innerHTML = itemToSearch.textContent.replace(/\d+(?:[,. ]\d+)*/, '<span>' + matches[0] + '</span>');
      var numberSpan = itemToSearch.querySelector('span');
      numberSpan.style.textAlign = matches[0] === matches['input'] ? null : 'end';

      if (!itemToSearch.classList.contains('text-gradient')) {
        numberSpan.style.display = 'inline-block';
        numberSpan.style.minWidth = numberSpan.clientWidth + 'px';
      }

      var toReplace = matches[0].replace(/[,\. ]+/, '');
      var charactersMatches = [];
      var re = /[,\. ]+/g;
      var m;
      while ((m = re.exec(matches[0])) !== null) {
        charactersMatches.push({ index: m.index, 0: m[0] });
      }

      animateProgress(function (progress) {
        var formattedString = Math.round(progress * parseInt(toReplace, 10)).toString();
        charactersMatches.forEach(function (character) {
          if (formattedString.length >= matches[0].length - character.index) {
            formattedString = formattedString.slice(0, character.index) + character[0] + formattedString.slice(character.index);
          }
        });
        numberSpan.textContent = progress === 1 ? matches[0] : formattedString;
      }, {
        duration: parseFloat(this.getAttribute('count-up')),
        easing: [0.16, 1, 0.3, 1]
      }).finished.then(function () {
        numberSpan.style.minWidth = null;
      });
    };
    return Ctor;
  })();

  /* ------------------------------------------------------------------
     <images-with-text-scrolling> — reveal + transition des images
     liée au scroll (portage verbatim, timeline → animations parallèles)
     ------------------------------------------------------------------ */
  var ImagesWithTextScrolling = (function () {
    function Ctor() {
      return Reflect.construct(HTMLElement, [], Ctor);
    }
    Ctor.prototype = Object.create(HTMLElement.prototype, { constructor: { value: Ctor } });
    Object.setPrototypeOf(Ctor, HTMLElement);

    Ctor.prototype.connectedCallback = function () {
      var self = this;
      inView(this, this._reveal.bind(this));

      if (this.hasAttribute('scrolling-experience')) {
        this._imageToTransitionItems = Array.prototype.slice.call(
          this.querySelectorAll('.images-scrolling-desktop__media-wrapper > :not(:first-child)')
        );
        this._mainImage = this.querySelector('.images-scrolling-desktop__media-wrapper > :first-child');
        this._contentItems = Array.prototype.slice.call(this.querySelectorAll('.images-scrolling__content'));

        if (this._imageToTransitionItems.length > 0) {
          window.addEventListener('scroll', throttle(this._onScroll.bind(this)), { passive: true });
        }
      }
    };

    Ctor.prototype._reveal = function () {
      Array.prototype.forEach.call(
        this.querySelectorAll('[reveal-on-scroll="true"]'),
        function (heading) { revealHeading(heading); }
      );
    };

    // timeline([[image, ...], [content, ..., at: '<']]) → les deux
    // animations démarrent ensemble
    Ctor.prototype._transition = function (image, content, imageEffect, show) {
      if (imageEffect === 'fade') {
        animateTo(image, { opacity: [null, show ? 1 : 0] }, { duration: 0.2 });
        animateTo(content, { opacity: [null, show ? 1 : 0] }, { duration: show ? 0.45 : 0.2 });
      } else {
        image.style.opacity = 1; // opacity: {duration: 0} dans Impact
        animateTo(image, {
          clipPath: show
            ? ['inset(100% 0 0 0)', 'inset(0 0 0 0)']
            : ['inset(0 0 0 0)', 'inset(100% 0 0 0)']
        }, { duration: 0.35, easing: [0.99, 0.01, 0.5, 0.94] });
        animateTo(content, { opacity: [null, show ? 1 : 0] }, { duration: show ? 0.45 : 0.2 });
      }
    };

    Ctor.prototype._onScroll = function () {
      if (!this._mainImage) return;
      var imageRect = this._mainImage.getBoundingClientRect();
      var imageBottom = imageRect.bottom;
      var imageEffect = this.getAttribute('scrolling-experience');

      for (var index = 0; index < this._contentItems.length; index++) {
        var contentItem = this._contentItems[index];
        var contentItemRect = contentItem.getBoundingClientRect();
        var image = this._imageToTransitionItems[index - 1];
        var content = this._contentItems[index];

        if (contentItemRect.top < imageBottom - imageRect.height * 0.1 && contentItemRect.bottom > imageBottom) {
          if (image && !image.classList.contains('is-visible')) {
            image.classList.add('is-visible');
            this._transition(image, content, imageEffect, true);
          }
          break;
        }

        if (contentItemRect.top > imageBottom - imageRect.height * 0.1) {
          if (image && image.classList.contains('is-visible')) {
            image.classList.remove('is-visible');
            this._transition(image, content, imageEffect, false);
          }
          break;
        }
      }
    };
    return Ctor;
  })();

  /* ------------------------------------------------------------------
     Enregistrement (mêmes noms d'éléments que dans Impact)
     ------------------------------------------------------------------ */
  if (!window.customElements.get('marquee-text')) {
    window.customElements.define('marquee-text', MarqueeText);
  }
  if (!window.customElements.get('scrolling-text')) {
    window.customElements.define('scrolling-text', ScrollingTextEl);
  }
  if (!window.customElements.get('impact-text')) {
    window.customElements.define('impact-text', ImpactText);
  }
  if (!window.customElements.get('images-with-text-scrolling')) {
    window.customElements.define('images-with-text-scrolling', ImagesWithTextScrolling);
  }
})();
